import * as React from "react";
import { Deployment } from "../../schema/types/workload/deployment";
import { linksOf, request, resourceLink } from "../../services/cpln";
import { parseLink } from "../../utils/linkParser/linkParser";

interface WorkloadDeploymentsMap {
  [_: string]: Deployment[];
}

interface DeploymentsReducerState {
  link: string;
  requestedLinks: string[];
  deploymentsMap: WorkloadDeploymentsMap;
  lastModified: string;
}

interface DeploymentsReducerType {
  state: DeploymentsReducerState;
  dispatch: React.Dispatch<DeploymentsReducerAction>;
  fetchDeployments: (workloadLinks: string[], intervalMs?: number, on404?: () => void) => () => void;
}

export function useDeploymentsReducer(): DeploymentsReducerType {
  const initialState: DeploymentsReducerState = {
    link: "",
    requestedLinks: [],
    deploymentsMap: {},
    lastModified: new Date().toISOString(),
  };
  const [state, dispatch] = React.useReducer(deploymentsReducer, initialState);
  const intervalIdRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  async function _fetchDeployments(links: string[], on404?: () => void) {
    const linksByGvc: { [_: string]: string[] } = {};
    for (const link of links) {
      const { gvc } = parseLink(link, { useInputCtx: true });
      if (!linksByGvc[gvc]) {
        linksByGvc[gvc] = [];
      }
      linksByGvc[gvc].push(link);
    }

    try {
      if (links.length > 1) {
        const gvcs = Object.keys(linksByGvc);

        const batchPromises: Promise<any>[] = [];
        for (const gvc of gvcs) {
          const url = resourceLink("gvc", gvc) + "/workload/-deployment";
          batchPromises.push(request({ url: url }));
        }

        const batchResponses = await Promise.allSettled(batchPromises);

        for (const link of links) {
          dispatch({
            type: DeploymentsReducerActionType.REGISTER_REQUESTED_WORKLOAD,
            payload: { link: link, deployments: [] },
          });
        }

        for (const gvcIndex in gvcs) {
          const gvc = gvcs[gvcIndex];
          const batchResponse = batchResponses[gvcIndex];
          if (batchResponse.status === "rejected") {
            continue;
          }

          // Dispatch once per GVC batch response with all deployments and requested workload links
          dispatch({
            type: DeploymentsReducerActionType.BATCH_DEPLOYMENT,
            payload: {
              link: "",
              deployments: batchResponse.value.data.items,
              requestedWorkloadLinks: linksByGvc[gvc],
            },
          });
        }
      } else {
        const link = links[0];
        const deploymentLink = link + "/deployment";
        const { data } = await request({ url: deploymentLink });
        const deployments = data.items;

        dispatch({
          type: DeploymentsReducerActionType.SET_DEPLOYMENTS,
          payload: { link: link, deployments: deployments },
        });
      }
    } catch (e: any) {
      // Check for 404 - workload was likely deleted
      if (e?.response?.status === 404) {
        if (links.length === 1) {
          dispatch({
            type: DeploymentsReducerActionType.SET_DEPLOYMENTS,
            payload: { link: links[0], deployments: [] },
          });
        }
        if (on404) {
          on404();
        }
        return;
      }
      console.error("Failed to fetch deployments:", e);
    }
  }

  function fetchDeployments(links: string[], intervalMs = 0, on404?: () => void) {
    // Clear any existing interval to prevent memory leaks
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }

    _fetchDeployments(links, on404);
    if (intervalMs < 1) {
      return () => {};
    }
    const id = setInterval(() => {
      _fetchDeployments(links, on404);
    }, intervalMs);
    intervalIdRef.current = id;
    return () => {
      clearInterval(id);
      intervalIdRef.current = null;
    };
  }

  // TODO can move starting a websocket and returning its unsubscribe

  return { state, dispatch, fetchDeployments };
}

export enum DeploymentsReducerActionType {
  SET_DEPLOYMENTS = "SET_DEPLOYMENTS",
  UPDATE_DEPLOYMENT = "UPDATE_DEPLOYMENT",
  CLEAR_DEPLOYMENTS = "CLEAR_DEPLOYMENTS",
  BATCH_DEPLOYMENT = "BATCH_DEPLOYMENT",
  REGISTER_REQUESTED_WORKLOAD = "REGISTER_REQUESTED_WORKLOAD",
}

interface DeploymentsReducerAction {
  type: DeploymentsReducerActionType;
  payload: {
    link: string;
    deployments: Deployment[];
    requestedWorkloadLinks?: string[]; // For BATCH_DEPLOYMENT to know which workloads to reset
  };
}

function deploymentsReducer(state: DeploymentsReducerState, action: DeploymentsReducerAction): DeploymentsReducerState {
  const { type, payload } = action;

  const now = new Date().toISOString();

  const requestedLinks = [...state.requestedLinks];
  if (!requestedLinks.includes(payload.link) && !!payload.link) {
    requestedLinks.push(payload.link);
  }

  switch (type) {
    case DeploymentsReducerActionType.CLEAR_DEPLOYMENTS:
      return {
        ...state,
        requestedLinks: [],
        deploymentsMap: {},
        lastModified: now,
      };
    case DeploymentsReducerActionType.SET_DEPLOYMENTS:
      return {
        ...state,
        requestedLinks: requestedLinks,
        deploymentsMap: {
          ...state.deploymentsMap,
          [payload.link]: payload.deployments,
        },
        lastModified: now,
      };
    case DeploymentsReducerActionType.UPDATE_DEPLOYMENT:
      const workloadDeployments: Deployment[] = [...(state.deploymentsMap[payload.link] || [])];
      const deployment = payload.deployments[0];

      const indexOf = workloadDeployments.findIndex((d) => d.name === deployment.name);
      if (indexOf >= 0) {
        // Replace completely - WebSocket sends full deployment state
        workloadDeployments.splice(indexOf, 1);
        workloadDeployments.push({ ...deployment, kind: "deployment" });
      } else {
        workloadDeployments.push({ ...deployment, kind: "deployment" });
      }

      return {
        ...state,
        requestedLinks: requestedLinks,
        deploymentsMap: {
          ...state.deploymentsMap,
          [payload.link]: workloadDeployments,
        },
        lastModified: now,
      };
    case DeploymentsReducerActionType.BATCH_DEPLOYMENT:
      // Build new deployments map in a single pass
      const newDeploymentsMap: { [_: string]: Deployment[] } = {};

      // First: reset all REQUESTED workloads to empty (handles complete deletion case)
      if (payload.requestedWorkloadLinks) {
        for (const workloadLink of payload.requestedWorkloadLinks) {
          newDeploymentsMap[workloadLink] = [];
        }
      }

      // Then: populate with deployments from response
      for (const deployment of payload.deployments) {
        const deploymentWorkloadLink = linksOf(deployment).workload;
        if (!deploymentWorkloadLink) {
          continue;
        }
        if (!newDeploymentsMap[deploymentWorkloadLink]) {
          newDeploymentsMap[deploymentWorkloadLink] = [];
        }
        newDeploymentsMap[deploymentWorkloadLink].push(deployment);
      }

      return {
        ...state,
        requestedLinks: requestedLinks,
        deploymentsMap: {
          ...state.deploymentsMap,
          ...newDeploymentsMap,
        },
        lastModified: now,
      };
    case DeploymentsReducerActionType.REGISTER_REQUESTED_WORKLOAD:
      return {
        ...state,
        requestedLinks: requestedLinks,
      };
    default:
      return state;
  }
}
